/**
 * Pyxel ランタイム（CDN 版）のバージョン解決。
 *
 * #746: 既定は 2.9.5 に固定する。#685 では 2.9.6 に固定していたが、pyxel 2.9.6 で
 * 同梱 Pyodide が 0.29.3 → 314.x（Python 3.14 / Emscripten 5.0.3）にジャンプし、
 * emscripten 5 世代の WASM 機能を iOS Safari が実行できず、iPad/iPhone で全作品が
 * `RuntimeError: Unreachable code should not be executed` でクラッシュする。
 * pyxel ≤2.9.5（pyodide 0.29.3）が iOS で動く最後の世代。
 *
 * `?pyxel=<x.y.z>` クエリ（または明示 prop）でバージョンを上書きできる。用途:
 *   - kitao 氏への再現報告（同一サイトで ?pyxel=2.9.6[落ちる] ↔ ?pyxel=2.9.5[動く] を URL 切替で提示）
 *   - 314 世代のどの版が iOS を壊したかの bisection
 *
 * セキュリティ: 上書き値は `^\d+\.\d+\.\d+$` で厳格に検証し、ホスト/リポジトリは
 * ハードコードする。可変なのは kitao/pyxel の**リリースタグのみ**なので、CSP の
 * `script-src` prefix と `<script src>` は常に同一バージョンで一致し、空白/セミコロン等を
 * 注入して CSP を破ることはできない（CSP 境界は不変）。
 * 詳細は doc/architecture/csp.md / doc/architecture/pyxel_runtime.md を参照。
 */

/** クエリ/prop 未指定時に使う既定の Pyxel バージョン（#746 で 2.9.6 → 2.9.5）。 */
export const DEFAULT_PYXEL_VERSION = '2.9.5'

/** `x.y.z` の 3 セグメント semver のみ許可（CDN タグに直接埋め込むため厳格に検証する）。 */
const PYXEL_VERSION_RE = /^\d+\.\d+\.\d+$/

/** 上書き値が安全な Pyxel バージョン文字列（`x.y.z`）かどうか。 */
export function isValidPyxelVersion(value: unknown): value is string {
  return typeof value === 'string' && PYXEL_VERSION_RE.test(value)
}

/**
 * 上書き候補（`route.query.pyxel` 等、string | string[] | null など任意の入力）を検証し、
 * 妥当なら採用、そうでなければ既定にフォールバックして最終バージョンを返す。
 */
export function resolvePyxelVersion(override?: unknown): string {
  return isValidPyxelVersion(override) ? override : DEFAULT_PYXEL_VERSION
}

/** 検証済みバージョンから Pyxel CDN のベース URL（末尾スラッシュ付き）を生成する。 */
export function pyxelCdnBase(version: string): string {
  return `https://cdn.jsdelivr.net/gh/kitao/pyxel@${version}/`
}

/**
 * 実行して Python が止まった理由 (#745)。
 *
 * Pyodide は Python の例外を JS の Error として投げてくるので、メッセージの中に
 * traceback がテキストで入っている。センパイの自発ヒントは「NameError なのか
 * AttributeError なのか」「どの名前でつまずいたのか」で言うことを変えるため、
 * ここで最低限だけ取り出す。読み取れなければ空で返す（ヒントが出ないだけ）。
 */
export interface PythonErrorInfo {
  errorType: string
  name: string
  message: string
}

/** traceback の最終行 `NameError: name 'x' is not defined` を拾う。 */
const PYTHON_ERROR_LINE_RE = /^([A-Za-z_][A-Za-z0-9_]*Error):\s*(.+)$/
/** `name 'scoer' is not defined` / `... has no attribute 'recct'` の引用名。 */
const QUOTED_NAME_RE = /'([^']+)'/g

export function parsePythonError(text: string): PythonErrorInfo | null {
  if (!text) return null
  let found: { errorType: string, detail: string } | null = null
  for (const line of text.split('\n')) {
    const m = PYTHON_ERROR_LINE_RE.exec(line.trim())
    // traceback は入れ子になりうるので、最後に出てきたものを採る。
    if (m) found = { errorType: m[1]!, detail: m[2]! }
  }
  if (!found) return null

  // 引用された名前のうち、いちばん後ろ = つまずいた名前（`module 'pyxel' has no
  // attribute 'recct'` なら recct のほう）。
  const quoted = [...found.detail.matchAll(QUOTED_NAME_RE)].map(m => m[1]!)
  return {
    errorType: found.errorType,
    name: quoted.length ? quoted[quoted.length - 1]! : '',
    message: found.detail,
  }
}
